/**
 * Contractor Desk client — crew roster, truck numbers, unpaid assignments.
 * Same identity headers as jobsService company APIs.
 */
import { legacyAuthHeader } from '../utils/legacyHostCompat';
import { hostWebApp } from './hostWebApp';
import { throwAccountAwareError } from './hostWebAppRequiredAccess';
import { getNativeIosAuthHeaders, getNativeIosLinkedUser } from './nativeApiRouting';
import type {
  ContractorAssignmentStatus,
  ContractorCrewGroup,
  ContractorDeskAssignment,
  ContractorDeskEmployee,
  ContractorDeskEmployeeRemoval,
  ContractorDeskOverview,
  ContractorDeskTruck,
  ContractorRosterRole,
} from '../../shared/contractorDesk';
import type {
  FieldDeskJobMasterPart,
  FieldDeskJobMasterPhoto,
  FieldDeskOverview,
  FieldDeskPartInput,
  FieldDeskPhotoKind,
} from '../../shared/fieldDesk';
import type {
  TruckCharge,
  TruckChargeJobMasterTotals,
  TruckChargesResponse,
} from '../../shared/truckCharges';

const LEGACY_AUTH_USER_ID_HEADER = legacyAuthHeader('User-Id');
const LEGACY_AUTH_INIT_DATA_HEADER = legacyAuthHeader('Init-Data');

export class ContractorDeskRequestError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function headers(): Record<string, string> {
  const linked = getNativeIosLinkedUser();
  const hostUserId = (hostWebApp && typeof hostWebApp.getUserId === 'function' ? hostWebApp.getUserId() : '') || '';
  const userId = hostUserId || linked?.userId || '';
  return {
    'Content-Type': 'application/json',
    [LEGACY_AUTH_USER_ID_HEADER]: userId,
    [LEGACY_AUTH_INIT_DATA_HEADER]: hostWebApp && typeof hostWebApp.getInitData === 'function' ? hostWebApp.getInitData() : '',
    ...(getNativeIosAuthHeaders() || {}),
  };
}

async function request<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(path, { ...init, headers: { ...headers(), ...(init.headers || {}) } });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    if (response.status === 401) throwAccountAwareError(data, response.status, fallback);
    throw new ContractorDeskRequestError(String(data?.error || fallback), response.status);
  }
  return data as T;
}

export async function getContractorDesk(): Promise<ContractorDeskOverview> {
  const data = await request<ContractorDeskOverview>('/api/contractor-desk', { method: 'GET' }, 'Could not load Contractor Desk');
  return {
    company: data.company,
    accessRole: data.accessRole || 'linked_member',
    ...(typeof data.canManage === 'boolean' ? { canManage: data.canManage } : {}),
    ...(typeof data.canAdminister === 'boolean' ? { canAdminister: data.canAdminister } : {}),
    ...(typeof data.canEditCrews === 'boolean' ? { canEditCrews: data.canEditCrews } : {}),
    ...(Array.isArray(data.scopeEmployeeIds) ? { scopeEmployeeIds: data.scopeEmployeeIds } : {}),
    ...(data.selfEmployeeId ? { selfEmployeeId: data.selfEmployeeId } : {}),
    ...(data.staffTier ? { staffTier: data.staffTier } : {}),
    employees: data.employees || [],
    crews: data.crews || [],
    linkedCrews: data.linkedCrews || [],
    trucks: data.trucks || [],
    assignments: data.assignments || [],
    linkedMembers: data.linkedMembers || [],
    jobMasters: data.jobMasters || [],
  };
}

export async function addContractorEmployee(payload: {
  firstName?: string;
  lastName?: string;
  rosterRole?: ContractorRosterRole;
  displayName?: string;
  phone?: string;
  title?: string;
  truckNumber?: string;
  linkedUserId?: string | null;
}): Promise<ContractorDeskEmployee> {
  const data = await request<{ employee: ContractorDeskEmployee }>(
    '/api/contractor-desk/employees',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not add employee',
  );
  return data.employee;
}

/** Edit any of first / last name, roster role, phone, title, linked app account (null unlinks). */
export async function updateContractorEmployee(
  employeeId: string,
  payload: {
    firstName?: string;
    lastName?: string;
    rosterRole?: ContractorRosterRole;
    displayName?: string;
    phone?: string | null;
    title?: string | null;
    linkedUserId?: string | null;
  },
): Promise<ContractorDeskEmployee> {
  const data = await request<{ employee: ContractorDeskEmployee }>(
    `/api/contractor-desk/employees/${encodeURIComponent(employeeId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not update employee',
  );
  return data.employee;
}

/** Soft delete: cancels open assignments, returns their Requests to New, unlinks trucks. */
export async function removeContractorEmployee(employeeId: string): Promise<ContractorDeskEmployeeRemoval> {
  const data = await request<{ removal?: ContractorDeskEmployeeRemoval }>(
    `/api/contractor-desk/employees/${encodeURIComponent(employeeId)}`,
    { method: 'DELETE' },
    'Could not remove employee',
  );
  return data.removal || { canceledAssignments: 0, returnedRequests: 0, unlinkedTrucks: 0 };
}

export async function addContractorTruck(payload: { truckNumber: string; details?: string | null; employeeId?: string | null; fleetAssetId?: string | null }): Promise<ContractorDeskTruck> {
  const data = await request<{ truck: ContractorDeskTruck }>(
    '/api/contractor-desk/trucks',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not add truck',
  );
  return data.truck;
}

export async function linkContractorTruck(truckId: string, employeeId: string | null): Promise<ContractorDeskTruck> {
  const data = await request<{ truck: ContractorDeskTruck }>(
    `/api/contractor-desk/trucks/${encodeURIComponent(truckId)}`,
    { method: 'PATCH', body: JSON.stringify({ employeeId }) },
    'Could not update truck',
  );
  return data.truck;
}

/** Edit any of truck number, details, driver (null unassigns), Fleet unit (null unlinks). */
export async function updateContractorTruck(
  truckId: string,
  payload: { truckNumber?: string; details?: string | null; employeeId?: string | null; fleetAssetId?: string | null },
): Promise<ContractorDeskTruck> {
  const data = await request<{ truck: ContractorDeskTruck }>(
    `/api/contractor-desk/trucks/${encodeURIComponent(truckId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not update truck',
  );
  return data.truck;
}

export async function removeContractorTruck(truckId: string): Promise<void> {
  await request(`/api/contractor-desk/trucks/${encodeURIComponent(truckId)}`, { method: 'DELETE' }, 'Could not remove truck');
}

export type ContractorAssignmentPayload = {
  dispatchId?: string;
  employeeId?: string | null;
  /** Individuals (roster employee ids). */
  employeeIds?: string[];
  /** Crew leads, meaning everyone on the crews they lead (lead included). */
  crewLeadEmployeeIds?: string[];
  /** Whole crews (lead + members). */
  crewIds?: string[];
  linkedCrewIds?: string[];
  linkedUserId?: string | null;
  linkedUserIds?: string[];
  siteName: string;
  apiNumber?: string | null;
  facilityId?: string | null;
  operatorName?: string | null;
  county?: string | null;
  state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  jobMasterId?: string | null;
  jobText?: string | null;
};

export async function createContractorAssignment(payload: ContractorAssignmentPayload): Promise<ContractorDeskAssignment> {
  const data = await request<{ assignment: ContractorDeskAssignment }>(
    '/api/contractor-desk/assignments',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not create assignment',
  );
  return data.assignment;
}

/** Multi-target assign: one assignment per person (individuals and/or crews via their lead). */
export async function createContractorAssignments(payload: ContractorAssignmentPayload): Promise<ContractorDeskAssignment[]> {
  const data = await request<{ assignment?: ContractorDeskAssignment; assignments?: ContractorDeskAssignment[] }>(
    '/api/contractor-desk/assignments',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not create assignment',
  );
  return data.assignments || (data.assignment ? [data.assignment] : []);
}

// ── Crews (name + crew lead + members) ──────────────────────────────────────

export async function createContractorCrew(payload: {
  name: string;
  leadEmployeeId?: string | null;
  memberIds?: string[];
}): Promise<ContractorCrewGroup> {
  const data = await request<{ crew: ContractorCrewGroup }>(
    '/api/contractor-desk/crews',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not create crew',
  );
  return data.crew;
}

export async function updateContractorCrew(
  crewId: string,
  payload: { name?: string; leadEmployeeId?: string | null; addMemberIds?: string[]; removeMemberIds?: string[] },
): Promise<ContractorCrewGroup> {
  const data = await request<{ crew: ContractorCrewGroup }>(
    `/api/contractor-desk/crews/${encodeURIComponent(crewId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not update crew',
  );
  return data.crew;
}

export async function removeContractorCrew(crewId: string): Promise<void> {
  await request(`/api/contractor-desk/crews/${encodeURIComponent(crewId)}`, { method: 'DELETE' }, 'Could not remove crew');
}

export async function setContractorAssignmentStatus(
  assignmentId: string,
  status: ContractorAssignmentStatus,
): Promise<ContractorDeskAssignment> {
  const data = await request<{ assignment: ContractorDeskAssignment }>(
    `/api/contractor-desk/assignments/${encodeURIComponent(assignmentId)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    'Could not update assignment',
  );
  return data.assignment;
}

// ── Job Master truck charges ────────────────────────────────────────────────
// Managers get prices; crew get amountCents: null (see src/shared/truckCharges.ts).

export async function getTruckCharges(jobMasterId?: string | null): Promise<TruckChargesResponse> {
  const query = jobMasterId ? `?jobMasterId=${encodeURIComponent(jobMasterId)}` : '';
  return request<TruckChargesResponse>(`/api/contractor-desk/truck-charges${query}`, { method: 'GET' }, 'Could not load truck charges');
}

export type TruckChargePayload = {
  jobMasterId: string;
  truckId?: string | null;
  driverEmployeeId?: string | null;
  workDate: string;
  hours?: number;
  miles?: number;
  days?: number;
  note?: string | null;
};

export async function createTruckCharge(
  payload: TruckChargePayload,
): Promise<{ charge: TruckCharge; jobMasterTotals: TruckChargeJobMasterTotals | null }> {
  const data = await request<{ charge: TruckCharge; jobMasterTotals?: TruckChargeJobMasterTotals | null }>(
    '/api/contractor-desk/truck-charges',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not save truck charge',
  );
  return { charge: data.charge, jobMasterTotals: data.jobMasterTotals || null };
}

export async function deleteTruckCharge(chargeId: string): Promise<void> {
  await request(`/api/contractor-desk/truck-charges/${encodeURIComponent(chargeId)}`, { method: 'DELETE' }, 'Could not delete truck charge');
}

// ── Field Desk (field hands) ────────────────────────────────────────────────

export async function getFieldDesk(focus: { jobMasterId?: string; actionId?: string } = {}): Promise<FieldDeskOverview> {
  const params = new URLSearchParams();
  if (focus.jobMasterId) params.set('jobMasterId', focus.jobMasterId);
  if (focus.actionId) params.set('actionId', focus.actionId);
  const query = params.size ? `?${params}` : '';
  const data = await request<FieldDeskOverview>(`/api/contractor-desk/field${query}`, { method: 'GET' }, 'Could not load Field Desk');
  return {
    company: data.company,
    accessRole: data.accessRole || 'linked_member',
    jobMasters: data.jobMasters || [],
    pendingRequestCount: data.pendingRequestCount,
    jobMastersTotal: data.jobMastersTotal,
    assignments: data.assignments || [],
    activeEntry: data.activeEntry || null,
    trucks: data.trucks || [],
    recentJsas: data.recentJsas || [],
    correctiveActions: data.correctiveActions || [],
  };
}

const fieldJobMasterPath = (jobMasterId: string) =>
  `/api/contractor-desk/field/job-masters/${encodeURIComponent(jobMasterId)}`;

/** Crew part line on an open Job Master — description / quantity / note only (no prices). */
export async function addFieldDeskPart(jobMasterId: string, part: FieldDeskPartInput): Promise<FieldDeskJobMasterPart[]> {
  const data = await request<{ parts?: FieldDeskJobMasterPart[] }>(`${fieldJobMasterPath(jobMasterId)}/parts`, {
    method: 'POST',
    body: JSON.stringify({ description: part.description, quantity: part.quantity, note: part.note || null }),
  }, 'Could not add part');
  return data.parts || [];
}

/** Remove your own part line (only before the owner prices it). */
export async function removeFieldDeskPart(jobMasterId: string, partId: string): Promise<FieldDeskJobMasterPart[]> {
  const data = await request<{ parts?: FieldDeskJobMasterPart[] }>(
    `${fieldJobMasterPath(jobMasterId)}/parts/${encodeURIComponent(partId)}`,
    { method: 'DELETE' },
    'Could not remove part',
  );
  return data.parts || [];
}

/** Field Desk auth headers without a JSON Content-Type (multipart uploads, image bytes). */
function fieldDeskMediaHeaders(): Record<string, string> {
  const { 'Content-Type': _json, ...rest } = headers();
  return rest;
}

/** Before / after photos on a Job Master. */
export async function listFieldDeskPhotos(jobMasterId: string): Promise<FieldDeskJobMasterPhoto[]> {
  const data = await request<{ photos?: FieldDeskJobMasterPhoto[] }>(`${fieldJobMasterPath(jobMasterId)}/photos`, { method: 'GET' }, 'Could not load photos');
  return data.photos || [];
}

export async function uploadFieldDeskPhoto(
  jobMasterId: string,
  kind: FieldDeskPhotoKind,
  file: File,
  location?: { lat: number; lng: number } | null,
): Promise<FieldDeskJobMasterPhoto[]> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('photo', file, file.name || `${kind}.jpg`);
  if (location && Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
    form.append('latitude', String(location.lat));
    form.append('longitude', String(location.lng));
  }
  const response = await fetch(`${fieldJobMasterPath(jobMasterId)}/photos`, { method: 'POST', body: form, headers: fieldDeskMediaHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    if (response.status === 401) throwAccountAwareError(data, response.status, 'Could not upload photo');
    throw new ContractorDeskRequestError(String(data?.error || 'Could not upload photo'), response.status);
  }
  return data.photos || [];
}

export async function removeFieldDeskPhoto(jobMasterId: string, photoId: string): Promise<FieldDeskJobMasterPhoto[]> {
  const data = await request<{ photos?: FieldDeskJobMasterPhoto[] }>(
    `${fieldJobMasterPath(jobMasterId)}/photos/${encodeURIComponent(photoId)}`,
    { method: 'DELETE' },
    'Could not remove photo',
  );
  return data.photos || [];
}

/** Photo bytes as an object URL (auth-gated route — caller revokes it). */
export async function fetchFieldDeskPhotoUrl(photo: FieldDeskJobMasterPhoto): Promise<string> {
  const response = await fetch(photo.url, { headers: fieldDeskMediaHeaders() });
  if (!response.ok) throw new ContractorDeskRequestError('Could not load photo', response.status);
  return URL.createObjectURL(await response.blob());
}

/** Link your JSA / inspection / a Crew truck to an open Job Master. Returns the fresh overview. */
export async function linkFieldDeskRecords(
  jobMasterId: string,
  body: { jsaId?: string | null; unlinkJsaId?: string; inspectionId?: string | null; unlinkInspectionId?: string; contractorDeskTruckId?: string | null },
): Promise<FieldDeskOverview> {
  const data = await request<FieldDeskOverview>(`${fieldJobMasterPath(jobMasterId)}/links`, {
    method: 'POST',
    body: JSON.stringify(body),
  }, 'Could not link record');
  return {
    company: data.company,
    accessRole: data.accessRole || 'linked_member',
    jobMasters: data.jobMasters || [],
    pendingRequestCount: data.pendingRequestCount,
    jobMastersTotal: data.jobMastersTotal,
    assignments: data.assignments || [],
    activeEntry: data.activeEntry || null,
    trucks: data.trucks || [],
    recentJsas: data.recentJsas || [],
    correctiveActions: data.correctiveActions || [],
  };
}

/** Close out an issued Job Master → finished + ready to bill. */
export async function closeOutFieldDeskJobMaster(jobMasterId: string, note?: string | null): Promise<void> {
  await request(`${fieldJobMasterPath(jobMasterId)}/close-out`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  }, 'Could not close out Job Master');
}

export type FieldDeskAssignmentJobMaster = {
  created: boolean;
  reused: boolean;
  assignmentId: string;
  requestId: string | null;
  jobMaster: { id: string; jmNumber: string; status: string; leaseFacilityName: string };
};

/**
 * My Jobs → Start work / Open Job Master on an assignment with no Job Master: opens (or reuses)
 * the job's active crew JM, prefilled from the assignment location and linked back to it.
 */
export async function openFieldDeskJobMasterForAssignment(
  assignmentId: string,
  requestId?: string | null,
): Promise<FieldDeskAssignmentJobMaster> {
  return request<FieldDeskAssignmentJobMaster>('/api/job-masters', {
    method: 'POST',
    body: JSON.stringify({ assignmentId, ...(requestId ? { requestId } : {}) }),
  }, 'Could not open a Job Master for this job');
}

// ── Job Masters — owner side (Contractor Desk owner billing, 2026-10-08) ─────────────────
// Owner-only company-dashboard routes; the server checks requireJobMasterOwnerCompany.

/** Reopen a finished (closed-out), not-invoiced Job Master → active. 409 once invoiced. */
export async function reopenOwnerJobMaster(jobMasterId: string, note?: string | null): Promise<void> {
  await request(`/api/company-dashboard/job-masters/${encodeURIComponent(jobMasterId)}/reopen`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  }, 'Could not reopen Job Master');
}

/** Price a part line (typically a crew-added "price needed" one). Owner only. */
export async function priceOwnerJobMasterPart(jobMasterId: string, partId: string, unitCostCents: number): Promise<void> {
  await request(`/api/company-dashboard/job-masters/${encodeURIComponent(jobMasterId)}/parts/${encodeURIComponent(partId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ unitCostCents }),
  }, 'Could not price part');
}

/** Set the rate tier a Job Master bills at (must match an active rate card). Owner only. */
export async function setOwnerJobMasterRateTier(jobMasterId: string, rateTier: string): Promise<string> {
  const data = await request<{ rateTier: string }>(`/api/company-dashboard/job-masters/${encodeURIComponent(jobMasterId)}/rate-tier`, {
    method: 'PATCH',
    body: JSON.stringify({ rateTier }),
  }, 'Could not set rate tier');
  return data.rateTier;
}

/** Bill a finished Job Master (freezes amounts). Optional rate tier is applied first. */
export async function billOwnerJobMaster(jobMasterId: string, options: { rateTier?: string | null; note?: string | null } = {}): Promise<{ billingNumber: string; totalCents: number }> {
  const data = await request<{ billingRecord: { billingNumber: string; totalCents: number } }>('/api/company-dashboard/job-master-billing-records', {
    method: 'POST',
    body: JSON.stringify({ jobMasterId, rateTier: options.rateTier || undefined, note: options.note || undefined }),
  }, 'Could not bill Job Master');
  return { billingNumber: data.billingRecord.billingNumber, totalCents: Number(data.billingRecord.totalCents || 0) };
}
